import { useEffect, useRef, useCallback } from 'react';
import { Map } from 'mapbox-gl';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/store';
import {
  hasActiveFrequency,
  groupBNsBySite,
  calculateAverageLocation,
} from '../utils/nearbyBNsUtils';
import {
  buildSiteFeatures,
  buildArcFeatures,
  getBNPropertyKey,
} from '../utils/mapUtils';
import { resolveBaseNode } from '../resolvers/BNResolver';
import {
  MAP_LAYER_IDS,
  MAP_SOURCE_IDS,
  MAP_INTERACTION_IDS,
  MAP_COLORS,
  MAP_STYLING,
  MAP_CLUSTERING,
  ARCS_MIN_ZOOM,
} from '../constants/mapConstants';
import {
  CIRCLE_GENERATION,
  DEFAULT_RADIUS_METERS,
  METERS_TO_DEGREES,
} from '../constants/geographicConstants';

export interface HeatmapHeight {
  above_open: number;
  above_roof: number;
}

export interface HeatmapOutput {
  free_space?: string;
  fixed_wireless?: string;
  mobility_macro?: string;
  mobility_micro?: string;
  fresnel_clearance?: string;
  path_loss?: string;
  link_speed?: string;
  confidence?: string;
}

export interface HeatmapFileType {
  gnp_output?: HeatmapOutput;
  aiml_output?: HeatmapOutput;
}

export interface HeatmapListItem {
  height: HeatmapHeight;
  base_url: string;
  geotiff?: HeatmapFileType;
  kmz?: HeatmapFileType;
  kml?: HeatmapFileType;
  overlay?: HeatmapFileType;
  legend?: HeatmapFileType;
  tif_tiles?: HeatmapFileType;
  kmz_tiles?: HeatmapFileType;
}

export interface BaseNode {
  serial: string;
  latitude: number;
  longitude: number;
  azimuth?: number | null;
  carrier?: Array<{
    frequency: number;
    bandwidth?: number;
    tx_power?: number;
  }>;
  heatmap_list?: HeatmapListItem[];
  site?: string;
  market?: string;
  operator?: string;
  region?: string;
  cell?: string;
  sector?: string;
  hostname?: string;
  height_agl?: number;
  height_gps?: number;
  ground_msl?: number;
  downtilt?: number;
  network_profile?: number;
  sector_cnt?: number;
  subscribers?: number;
  planning_id?: string;
  part_number?: string;
  firmware?: string;
  timestamp?: string;
  data_issues?: string;
  heatmap_legends?: {
    base_url?: string;
    confidence?: string;
    fresnel_clearance?: string;
    path_loss_3ghz?: string;
    path_loss_5ghz?: string;
    linkspeed_x2?: string;
    linkspeed_3ghz?: string;
    linkspeed_5ghz?: string;
  };
  [key: string]: any;
}

export interface BaseNodeList {
  bn_list: BaseNode[];
  radius?: number;
  showBNRadius?: boolean;
  rectangleAPICall?: boolean;
  suffixes?: any;
  legends?: any;
}

export interface SiteInfo {
  siteId: string;
  baseNodes: BaseNode[];
  location: { latitude: number; longitude: number };
  selectedSerial?: string;
}

interface UseNearbyBaseNodesOptions {
  map: Map | null;
  baseNodeList: BaseNode[] | null;
  showBNRadius: boolean;
  onSiteClick?: (site: SiteInfo) => void;
  onArcRightClick?: (node: BaseNode) => void;
}

// Constant arrays for map cleanup - defined at module level to avoid recreation
const INTERACTIONS_TO_REMOVE = [
  MAP_INTERACTION_IDS.SITES_CLUSTERS_CLICK,
  MAP_INTERACTION_IDS.SITES_UNCLUSTERED_CLICK,
  MAP_INTERACTION_IDS.SITES_UNCLUSTERED_HOVER,
  MAP_INTERACTION_IDS.SITES_UNCLUSTERED_LEAVE,
  MAP_INTERACTION_IDS.ARCS_FILL_CLICK,
  MAP_INTERACTION_IDS.ARCS_FILL_CONTEXTMENU,
  MAP_INTERACTION_IDS.ARCS_FILL_HOVER,
  MAP_INTERACTION_IDS.ARCS_FILL_LEAVE,
] as const;

const LAYERS_TO_REMOVE: readonly string[] = [
  MAP_LAYER_IDS.SITES_CLUSTERS,
  MAP_LAYER_IDS.SITES_CLUSTER_COUNT,
  MAP_LAYER_IDS.SITES_UNCLUSTERED,
  MAP_LAYER_IDS.SITES_BN_COUNT,
  MAP_LAYER_IDS.RADIUS_CIRCLE_FILL,
  MAP_LAYER_IDS.RADIUS_CIRCLE_STROKE,
  MAP_LAYER_IDS.ARCS_FILL,
  MAP_LAYER_IDS.ARCS_STROKE,
] as const;

const SOURCES_TO_REMOVE = [
  MAP_SOURCE_IDS.SITES,
  MAP_SOURCE_IDS.RADIUS_CIRCLE,
  MAP_SOURCE_IDS.ARCS,
] as const;

const ARCS_INTERACTIONS_TO_REMOVE = [
  MAP_INTERACTION_IDS.ARCS_FILL_CLICK,
  MAP_INTERACTION_IDS.ARCS_FILL_CONTEXTMENU,
  MAP_INTERACTION_IDS.ARCS_FILL_HOVER,
  MAP_INTERACTION_IDS.ARCS_FILL_LEAVE,
] as const;

export const useNearbyBaseNodes = ({
  map,
  baseNodeList,
  showBNRadius,
  onSiteClick,
  onArcRightClick,
}: UseNearbyBaseNodesOptions) => {
  const activeBands = useAppSelector(
    (state: RootState) => state.servicePredictor.filters.activeBands
  );
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const filteredNodesRef = useRef<BaseNode[]>([]);
  const pinnedArcIdRef = useRef<string | null>(null);

  const hasActiveFrequencyHelper = useCallback(
    (node: BaseNode): boolean => {
      return hasActiveFrequency(node, activeBands);
    },
    [activeBands]
  );

  const removeInteractions = useCallback(
    (interactionIds: readonly string[]) => {
      if (!map) return;
      interactionIds.forEach((interactionId) => {
        try {
          map.removeInteraction(interactionId);
        } catch (e) {}
      });
    },
    [map]
  );

  const removeLayers = useCallback(
    (layerIds: readonly string[]) => {
      if (!map) return;
      layerIds.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      });
    },
    [map]
  );

  const removeSources = useCallback(
    (sourceIds: readonly string[]) => {
      if (!map) return;
      sourceIds.forEach((sourceId) => {
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      });
    },
    [map]
  );

  const clearBaseNodes = useCallback(() => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    pinnedArcIdRef.current = null;

    if (!map) return;

    removeInteractions(INTERACTIONS_TO_REMOVE);
    removeLayers(LAYERS_TO_REMOVE);
    removeSources(SOURCES_TO_REMOVE);
  }, [map, removeInteractions, removeLayers, removeSources]);

  const addArcFeatureSource = useCallback(
    (map: Map, arcFeatures: GeoJSON.Feature[]) => {
      const arcsSourceId = MAP_SOURCE_IDS.ARCS;
      if (!map.getSource(arcsSourceId)) {
        map.addSource(arcsSourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: arcFeatures,
          },
          generateId: false,
          promoteId: 'arcId',
        });
      } else {
        const source = map.getSource(arcsSourceId) as mapboxgl.GeoJSONSource;
        source.setData({
          type: 'FeatureCollection',
          features: arcFeatures,
        });
      }
    },
    []
  );

  const addArcFillLayer = useCallback((map: Map) => {
    const arcsSourceId = MAP_SOURCE_IDS.ARCS;
    const arcsFillLayerId = MAP_LAYER_IDS.ARCS_FILL;

    if (!map.getLayer(arcsFillLayerId)) {
      const beforeId = map.getLayer(MAP_LAYER_IDS.SITES_CLUSTERS)
        ? MAP_LAYER_IDS.SITES_CLUSTERS
        : map.getLayer(MAP_LAYER_IDS.SITES_UNCLUSTERED)
          ? MAP_LAYER_IDS.SITES_UNCLUSTERED
          : map.getLayer(MAP_LAYER_IDS.SITES_BN_COUNT)
            ? MAP_LAYER_IDS.SITES_BN_COUNT
            : undefined;

      map.addLayer(
        {
          id: arcsFillLayerId,
          type: 'fill',
          source: arcsSourceId,
          minzoom: ARCS_MIN_ZOOM,
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              MAP_COLORS.ARC_HIGHLIGHTED,
              MAP_COLORS.ARC_DEFAULT,
            ],
            'fill-opacity': [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              MAP_STYLING.ARC_FILL_OPACITY_HIGHLIGHTED,
              MAP_STYLING.ARC_FILL_OPACITY_DEFAULT,
            ],
          },
        },
        beforeId
      );
    }
  }, []);

  const addArcLineLayer = useCallback((map: Map) => {
    const arcsSourceId = MAP_SOURCE_IDS.ARCS;
    const arcsStrokeLayerId = MAP_LAYER_IDS.ARCS_STROKE;

    if (!map.getLayer(arcsStrokeLayerId)) {
      const beforeId = map.getLayer(MAP_LAYER_IDS.SITES_CLUSTERS)
        ? MAP_LAYER_IDS.SITES_CLUSTERS
        : map.getLayer(MAP_LAYER_IDS.SITES_UNCLUSTERED)
          ? MAP_LAYER_IDS.SITES_UNCLUSTERED
          : map.getLayer(MAP_LAYER_IDS.SITES_BN_COUNT)
            ? MAP_LAYER_IDS.SITES_BN_COUNT
            : undefined;

      map.addLayer(
        {
          id: arcsStrokeLayerId,
          type: 'line',
          source: arcsSourceId,
          minzoom: ARCS_MIN_ZOOM,
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              MAP_COLORS.ARC_STROKE_HIGHLIGHTED,
              MAP_COLORS.ARC_STROKE_DEFAULT,
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              MAP_STYLING.ARC_LINE_WIDTH_HIGHLIGHTED,
              MAP_STYLING.ARC_LINE_WIDTH_DEFAULT,
            ],
            'line-opacity': MAP_STYLING.ARC_LINE_OPACITY,
          },
        },
        beforeId
      );
    }
  }, []);

  const addArcFillContextClickInteraction = useCallback(
    (
      map: Map,
      onArcRightClick?: (node: BaseNode) => void,
      onSiteClick?: (site: SiteInfo) => void
    ) => {
      const arcsFillLayerId = MAP_LAYER_IDS.ARCS_FILL;

      // Remove existing interaction first to avoid duplicates
      try {
        map.removeInteraction(MAP_INTERACTION_IDS.ARCS_FILL_CONTEXTMENU);
      } catch (e) {
        // Interaction might not exist, ignore
      }

      map.addInteraction(MAP_INTERACTION_IDS.ARCS_FILL_CONTEXTMENU, {
        type: 'contextmenu',
        target: { layerId: arcsFillLayerId },
        handler: ({ feature, originalEvent }) => {
          if (!feature) {
            return;
          }

          if (
            originalEvent &&
            typeof originalEvent.preventDefault === 'function'
          ) {
            originalEvent.preventDefault();
          }

          const properties = feature.properties as any;
          const serial = properties.serial;
          const siteId = properties.siteId;
          const arcId = feature.id as string;

          if (pinnedArcIdRef.current === arcId) {
            map.setFeatureState(feature, { highlight: false });
            pinnedArcIdRef.current = null;
          } else {
            if (pinnedArcIdRef.current) {
              const arcsSourceId = MAP_SOURCE_IDS.ARCS;
              const source = map.getSource(
                arcsSourceId
              ) as mapboxgl.GeoJSONSource;
              if (source) {
                try {
                  const previousFeature = {
                    id: pinnedArcIdRef.current,
                    source: arcsSourceId,
                  };
                  map.setFeatureState(previousFeature, { highlight: false });
                } catch (e) {
                  // Feature might not exist anymore, ignore
                }
              }
            }

            map.setFeatureState(feature, { highlight: true });
            pinnedArcIdRef.current = arcId;
          }

          const filteredNodes = filteredNodesRef.current;
          const bn = filteredNodes.find((n) => n.serial === serial);

          if (bn && onArcRightClick) {
            onArcRightClick(bn);
          }

          if (siteId && onSiteClick) {
            const bnsBySite = groupBNsBySite(filteredNodes);
            const siteBNs = bnsBySite[siteId];

            if (siteBNs && siteBNs.length > 0) {
              const siteLocation = calculateAverageLocation(siteBNs);
              if (siteLocation) {
                const siteInfo: SiteInfo = {
                  siteId,
                  baseNodes: siteBNs,
                  location: siteLocation,
                  selectedSerial: serial,
                };
                onSiteClick(siteInfo);
              }
            }
          }
        },
      });
    },
    []
  );

  const addArcFillOnMouseEvent = useCallback((map: Map) => {
    const arcsSourceId = MAP_SOURCE_IDS.ARCS;
    const arcsFillLayerId = MAP_LAYER_IDS.ARCS_FILL;

    // Remove existing interaction first to avoid duplicates
    try {
      map.removeInteraction(MAP_INTERACTION_IDS.ARCS_FILL_HOVER);
    } catch (e) {
      // Interaction might not exist, ignore
    }

    // Hover interactions for arcs
    map.addInteraction(MAP_INTERACTION_IDS.ARCS_FILL_HOVER, {
      type: 'mouseenter',
      target: { layerId: arcsFillLayerId },
      handler: ({ feature }) => {
        if (!feature) return;
        map.setFeatureState(feature, { highlight: true });
        map.getCanvas().style.cursor = 'pointer';
      },
    });
  }, []);

  const addArcFillOffMouseEvent = useCallback((map: Map) => {
    const arcsFillLayerId = MAP_LAYER_IDS.ARCS_FILL;

    // Remove existing interaction first to avoid duplicates
    try {
      map.removeInteraction(MAP_INTERACTION_IDS.ARCS_FILL_LEAVE);
    } catch (e) {
      // Interaction might not exist, ignore
    }

    map.addInteraction(MAP_INTERACTION_IDS.ARCS_FILL_LEAVE, {
      type: 'mouseleave',
      target: { layerId: arcsFillLayerId },
      handler: ({ feature }) => {
        if (!feature) return;
        const arcId = feature.id as string;
        if (pinnedArcIdRef.current === arcId) {
          // Keep the highlight, just reset cursor
          map.getCanvas().style.cursor = '';
          return false;
        }
        map.setFeatureState(feature, { highlight: false });
        map.getCanvas().style.cursor = '';
        return false;
      },
    });
  }, []);

  const addSiteFeatureSource = useCallback(
    (map: Map, siteFeatures: GeoJSON.Feature[]) => {
      if (!map.getSource(MAP_SOURCE_IDS.SITES)) {
        map.addSource(MAP_SOURCE_IDS.SITES, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: siteFeatures,
          },
          generateId: false,
          promoteId: 'siteId',
          cluster: true,
          clusterMaxZoom: MAP_CLUSTERING.CLUSTER_MAX_ZOOM,
          clusterRadius: MAP_CLUSTERING.CLUSTER_RADIUS,
          clusterProperties: {
            totalBNCount: ['+', ['get', 'bnCount']],
          },
        });
      } else {
        const source = map.getSource(
          MAP_SOURCE_IDS.SITES
        ) as mapboxgl.GeoJSONSource;
        source.setData({
          type: 'FeatureCollection',
          features: siteFeatures,
        });
      }
    },
    []
  );

  const addSiteClustersLayer = useCallback((map: Map) => {
    if (!map.getLayer(MAP_LAYER_IDS.SITES_CLUSTERS)) {
      map.addLayer({
        id: MAP_LAYER_IDS.SITES_CLUSTERS,
        type: 'circle',
        source: MAP_SOURCE_IDS.SITES,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': MAP_COLORS.SITE_CLUSTER,
          'circle-radius': [
            'step',
            ['get', 'totalBNCount'],
            MAP_CLUSTERING.CLUSTER_STEP_RADIUS_10,
            10,
            MAP_CLUSTERING.CLUSTER_STEP_RADIUS_30,
          ],
        },
      });
    }
  }, []);

  const addSiteClusterCountLayer = useCallback((map: Map) => {
    if (!map.getLayer(MAP_LAYER_IDS.SITES_CLUSTER_COUNT)) {
      map.addLayer({
        id: MAP_LAYER_IDS.SITES_CLUSTER_COUNT,
        type: 'symbol',
        source: MAP_SOURCE_IDS.SITES,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['to-string', ['get', 'totalBNCount']],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': MAP_STYLING.TEXT_SIZE,
        },
        paint: {
          'text-color': MAP_COLORS.TEXT_WHITE,
        },
      });
    }
  }, []);

  const addSiteUnclusteredLayer = useCallback((map: Map) => {
    if (!map.getLayer(MAP_LAYER_IDS.SITES_UNCLUSTERED)) {
      map.addLayer({
        id: MAP_LAYER_IDS.SITES_UNCLUSTERED,
        type: 'circle',
        source: MAP_SOURCE_IDS.SITES,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            MAP_COLORS.SITE_HIGHLIGHTED,
            MAP_COLORS.SITE_DEFAULT,
          ],
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            MAP_STYLING.SITE_CIRCLE_RADIUS_HIGHLIGHTED,
            MAP_STYLING.SITE_CIRCLE_RADIUS_DEFAULT,
          ],
          'circle-stroke-width': MAP_STYLING.SITE_CIRCLE_STROKE_WIDTH,
          'circle-stroke-color': MAP_COLORS.TEXT_WHITE,
          'circle-opacity': 1,
        },
      });
    }
  }, []);

  const addBNCountLayer = useCallback((map: Map) => {
    if (!map.getLayer(MAP_LAYER_IDS.SITES_BN_COUNT)) {
      map.addLayer({
        id: MAP_LAYER_IDS.SITES_BN_COUNT,
        type: 'symbol',
        source: MAP_SOURCE_IDS.SITES,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': ['get', 'bnCount'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': MAP_STYLING.TEXT_SIZE,
          'text-offset': [0, 0],
        },
        paint: {
          'text-color': MAP_COLORS.TEXT_WHITE,
        },
      });
    }
  }, []);

  const addSiteUnclusteredClickInteraction = useCallback(
    (map: Map, onSiteClick?: (site: SiteInfo) => void) => {
      try {
        map.removeInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_CLICK);
      } catch (e) {}

      map.addInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_CLICK, {
        type: 'click',
        target: { layerId: MAP_LAYER_IDS.SITES_UNCLUSTERED },
        handler: ({ feature }) => {
          if (!feature) {
            return;
          }

          const properties = feature.properties as any;
          const siteId = properties.siteId;

          const siteBaseNodes: BaseNode[] = [];
          for (let i = 0; i < 8; i++) {
            const propertyKey = getBNPropertyKey(i, 'node');
            const nodeValue = properties[propertyKey];

            const node = resolveBaseNode(nodeValue, propertyKey);

            if (node) {
              siteBaseNodes.push(node);
            }
          }

          if (
            onSiteClick &&
            siteId &&
            siteBaseNodes.length > 0 &&
            feature.geometry
          ) {
            const coordinates =
              feature.geometry.type === 'Point'
                ? (feature.geometry as GeoJSON.Point).coordinates
                : null;

            if (coordinates && coordinates.length >= 2) {
              const [longitude, latitude] = coordinates;
              if (!isNaN(longitude) && !isNaN(latitude)) {
                const siteInfo: SiteInfo = {
                  siteId,
                  baseNodes: siteBaseNodes,
                  location: { latitude, longitude },
                };
                onSiteClick(siteInfo);
              }
            }
          }
        },
      });
    },
    []
  );

  const addSiteClusteredClickInteraction = useCallback((map: Map) => {
    try {
      map.removeInteraction(MAP_INTERACTION_IDS.SITES_CLUSTERS_CLICK);
    } catch (e) {
      // Interaction might not exist, ignore
    }

    map.addInteraction(MAP_INTERACTION_IDS.SITES_CLUSTERS_CLICK, {
      type: 'click',
      target: { layerId: MAP_LAYER_IDS.SITES_CLUSTERS },
      handler: ({ feature, lngLat }) => {
        if (!feature) return;

        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource(
          MAP_SOURCE_IDS.SITES
        ) as mapboxgl.GeoJSONSource;

        if (
          source &&
          'getClusterExpansionZoom' in source &&
          typeof clusterId === 'number'
        ) {
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || zoom === undefined || zoom === null) return;

            map.easeTo({
              center: lngLat,
              zoom: Math.max(zoom, ARCS_MIN_ZOOM),
            });
          });
        }
      },
    });
  }, []);

  const addSiteUnclusteredOnMouseEvent = useCallback((map: Map) => {
    // Remove existing interaction first to avoid duplicates
    try {
      map.removeInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_HOVER);
    } catch (e) {
      // Interaction might not exist, ignore
    }

    // Sites unclustered hover interactions
    map.addInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_HOVER, {
      type: 'mouseenter',
      target: { layerId: MAP_LAYER_IDS.SITES_UNCLUSTERED },
      handler: ({ feature }) => {
        if (!feature || feature.id === undefined) return;
        map.setFeatureState(feature, { highlight: true });
        map.getCanvas().style.cursor = 'pointer';
      },
    });
  }, []);

  const addSiteUnclusteredOffMouseEvent = useCallback((map: Map) => {
    // Remove existing interaction first to avoid duplicates
    try {
      map.removeInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_LEAVE);
    } catch (e) {
      // Interaction might not exist, ignore
    }

    map.addInteraction(MAP_INTERACTION_IDS.SITES_UNCLUSTERED_LEAVE, {
      type: 'mouseleave',
      target: { layerId: MAP_LAYER_IDS.SITES_UNCLUSTERED },
      handler: ({ feature }) => {
        if (!feature || feature.id === undefined) return;
        map.setFeatureState(feature, { highlight: false });
        map.getCanvas().style.cursor = '';
        return false;
      },
    });
  }, []);

  useEffect(() => {
    if (!map || !baseNodeList || !showBNRadius) {
      clearBaseNodes();
      return;
    }

    if (baseNodeList.length === 0) {
      clearBaseNodes();
      return;
    }

    const filteredNodes = baseNodeList.filter(hasActiveFrequencyHelper);

    filteredNodesRef.current = filteredNodes;

    if (filteredNodes.length === 0) {
      clearBaseNodes();
      return;
    }

    const bnsBySite = groupBNsBySite(filteredNodes);

    const siteFeatures = buildSiteFeatures(bnsBySite);
    const arcFeatures = buildArcFeatures(bnsBySite);

    const arcsSourceId = MAP_SOURCE_IDS.ARCS;
    const arcsFillLayerId = MAP_LAYER_IDS.ARCS_FILL;
    const arcsStrokeLayerId = MAP_LAYER_IDS.ARCS_STROKE;

    if (arcFeatures.length > 0) {
      addArcFeatureSource(map, arcFeatures);

      if (pinnedArcIdRef.current) {
        const pinnedArcExists = arcFeatures.some(
          (feature) => feature.id === pinnedArcIdRef.current
        );
        if (pinnedArcExists) {
          setTimeout(() => {
            try {
              const pinnedFeature = {
                id: pinnedArcIdRef.current!,
                source: arcsSourceId,
              };
              map.setFeatureState(pinnedFeature, { highlight: true });
            } catch (e) {
              pinnedArcIdRef.current = null;
            }
          }, 0);
        } else {
          pinnedArcIdRef.current = null;
        }
      }

      if (!map.getLayer(arcsFillLayerId)) {
        addArcFillLayer(map);
        addArcLineLayer(map);
        addArcFillContextClickInteraction(map, onArcRightClick, onSiteClick);
        addArcFillOnMouseEvent(map);
        addArcFillOffMouseEvent(map);
      }
    } else {
      // Remove interactions
      removeInteractions(ARCS_INTERACTIONS_TO_REMOVE);

      if (map.getLayer(arcsFillLayerId)) {
        map.removeLayer(arcsFillLayerId);
      }
      if (map.getLayer(arcsStrokeLayerId)) {
        map.removeLayer(arcsStrokeLayerId);
      }
      if (map.getSource(arcsSourceId)) {
        map.removeSource(arcsSourceId);
      }
    }

    addSiteFeatureSource(map, siteFeatures);

    if (
      !map.getSource(MAP_SOURCE_IDS.SITES) ||
      !map.getLayer(MAP_LAYER_IDS.SITES_CLUSTERS)
    ) {
      addSiteClustersLayer(map);
      addSiteClusterCountLayer(map);
      addSiteUnclusteredLayer(map);
      addBNCountLayer(map);
    }

    // Register interactions for sites (always register, even if source already exists)
    addSiteClusteredClickInteraction(map);
    addSiteUnclusteredClickInteraction(map, onSiteClick);
    addSiteUnclusteredOnMouseEvent(map);
    addSiteUnclusteredOffMouseEvent(map);

    // if (radius && filteredNodes.length > 0) {
    //   const centerPoint = filteredNodes.reduce(
    //     (acc, node) => ({
    //       lat: acc.lat + node.latitude,
    //       lng: acc.lng + node.longitude,
    //     }),
    //     { lat: 0, lng: 0 }
    //   );

    // const center: [number, number] = [
    //   centerPoint.lng / filteredNodes.length,
    //   centerPoint.lat / filteredNodes.length,
    // ];

    // const steps = CIRCLE_GENERATION.STEPS;
    // const circleCoordinates: number[][] = [];
    // const radiusInMeters = radius || DEFAULT_RADIUS_METERS;
    // const radiusInDegrees = radiusInMeters * METERS_TO_DEGREES;

    // for (let i = 0; i <= steps; i++) {
    //   const angle = (i * 2 * Math.PI) / steps;
    //   const latOffset = radiusInDegrees * Math.cos(angle);
    //   const lngOffset =
    //     (radiusInDegrees * Math.sin(angle)) / Math.cos((center[1] * Math.PI) / 180);
    //   circleCoordinates.push([center[0] + lngOffset, center[1] + latOffset]);
    // }

    // circleCoordinates.push(circleCoordinates[0]);

    // if (!map.getSource(MAP_SOURCE_IDS.RADIUS_CIRCLE)) {
    //   map.addSource(MAP_SOURCE_IDS.RADIUS_CIRCLE, {
    //     type: 'geojson',
    //     data: {
    //       type: 'Feature',
    //       properties: {},
    //       geometry: {
    //         type: 'Polygon',
    //         coordinates: [circleCoordinates],
    //       },
    //     },
    //   });

    //   map.addLayer({
    //     id: MAP_LAYER_IDS.RADIUS_CIRCLE_FILL,
    //     type: 'fill',
    //     source: MAP_SOURCE_IDS.RADIUS_CIRCLE,
    //     paint: {
    //       'fill-color': '#4285F4',
    //       'fill-opacity': 0.1,
    //     },
    //   });

    //   map.addLayer({
    //     id: MAP_LAYER_IDS.RADIUS_CIRCLE_STROKE,
    //     type: 'line',
    //     source: MAP_SOURCE_IDS.RADIUS_CIRCLE,
    //     paint: {
    //       'line-color': '#4285F4',
    //       'line-width': 2,
    //       'line-opacity': 0.6,
    //     },
    //   });
    // } else {
    //   const source = map.getSource(MAP_SOURCE_IDS.RADIUS_CIRCLE) as mapboxgl.GeoJSONSource;
    //   source.setData({
    //     type: 'Feature',
    //     properties: {},
    //     geometry: {
    //       type: 'Polygon',
    //       coordinates: [circleCoordinates],
    //     },
    //   });
    // }
    // }
  }, [
    map,
    baseNodeList,
    activeBands,
    showBNRadius,
    hasActiveFrequencyHelper,
    onSiteClick,
    onArcRightClick,
    clearBaseNodes,
    removeInteractions,
    addArcFeatureSource,
    addArcFillLayer,
    addArcLineLayer,
    addArcFillContextClickInteraction,
    addArcFillOnMouseEvent,
    addArcFillOffMouseEvent,
    addSiteFeatureSource,
    addSiteClustersLayer,
    addSiteClusterCountLayer,
    addSiteUnclusteredLayer,
    addBNCountLayer,
    addSiteUnclusteredClickInteraction,
    addSiteClusteredClickInteraction,
    addSiteUnclusteredOnMouseEvent,
    addSiteUnclusteredOffMouseEvent,
  ]);

  useEffect(() => {
    return () => {
      clearBaseNodes();
    };
  }, [clearBaseNodes]);
};
